"use-client"

import { GetWebPageData, getWebPage, getWebPages } from "@/api";
import { Request } from "../components/CRUD"
import { useEffect, useState } from "react";
import { appState } from "@/app/appState";
import { page_artifact } from "@/app/typing/artifacts";
import { ForwardRefEditor } from "@/components/richtext/ForwardRefEditor";
import { codeBlockPlugin, codeMirrorPlugin, headingsPlugin, listsPlugin, quotePlugin, tablePlugin, thematicBreakPlugin } from "@mdxeditor/editor";
import { ActionHandler } from "@/components/UI/tools";
import { Link, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { PublicPageShell } from "@/components/PublicPageShell";

const PLACEHOLDER_CTA = /\s*\[[^\]]*Læs mere her[^\]]*\]\s*/gi;

function stripPlaceholderCtas(markdown: string): string {
    return markdown.replace(PLACEHOLDER_CTA, "\n").replace(/\n{3,}/g, "\n\n").trim();
}






export const HomePage = observer(function HomePage({ id }: { id: number }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false)
    const [pageArtifact, setPageArtifact] = useState<page_artifact>()
    const [homePageCards, setHomePageCards] = useState<page_artifact[]>([])


    const getArtifact = Request<GetWebPageData, any>(
        getWebPage,
        () => ({
            body: {
                id: id!
            },
        }),
        setLoading,
        (data: any) => {
            setPageArtifact(data.artifact);
        },
        (error) => {
            setPageArtifact(undefined)
            appState.setModal({ title: appState.t("common.error"), body: appState.t("marketing.errorGetPage") });
        },
        () => id != undefined && id > 0,
    )

    useEffect(() => {
        getArtifact()
        const getHomePageCards = async () => {
            try {
                const cardsListResponse = await getWebPages({});
                const artifacts = ((cardsListResponse.data as any)?.artifacts ?? []) as { id: number; variable_name: string }[];
                const sortedCardEntries = artifacts
                    .map((artifact) => {
                        const match = artifact.variable_name.match(/^homepage_card(\d+)$/);
                        return {
                            ...artifact,
                            cardIndex: match ? Number(match[1]) : null,
                        };
                    })
                    .filter((artifact) => artifact.cardIndex !== null)
                    .sort((a, b) => (a.cardIndex as number) - (b.cardIndex as number));

                const cardsResponse = await Promise.all(
                    sortedCardEntries.map((artifact) =>
                        getWebPage({
                            body: { id: artifact.id },
                        })
                    )
                );

                const cards = cardsResponse
                    .map((response) => (response.data as any)?.artifact as page_artifact | undefined)
                    .filter((artifact): artifact is page_artifact => Boolean(artifact));

                setHomePageCards(cards);
            } catch {
                setHomePageCards([]);
            }
        };

        getHomePageCards();
    }, [])

    return (
        <PublicPageShell
            footerLanguageSwitcher
            contentClassName="justify-center items-center p-3 gap-2"
        >
            {loading ? null : (
                <>
                    <div className="public-card flex flex-col gap-3">
                        <h1 className="display">eyka</h1>
                        <div className="w-fit h-fit">
                            <ForwardRefEditor
                                markdown={stripPlaceholderCtas(pageArtifact?.content ?? "")}
                                readOnly={true}
                                plugins={[
                                    headingsPlugin(),
                                    listsPlugin(),
                                    quotePlugin(),
                                    thematicBreakPlugin(),
                                    tablePlugin(),
                                    codeBlockPlugin({ defaultCodeBlockLanguage: "" }),
                                    codeMirrorPlugin({ codeBlockLanguages: { "": "Plain Text" } }),
                                ]}
                                contentEditableClassName="w-fit serif public-lead"
                            />
                        </div>
                        <div className="flex flex-row items-center justify-center gap-1">
                            <ActionHandler funq={() => navigate("/signin")} disabled={false}>
                                {appState.t("marketing.signIn")}
                            </ActionHandler>
                            <ActionHandler funq={() => navigate("/signup")} disabled={false}>
                                {appState.t("marketing.signUp")}
                            </ActionHandler>
                        </div>
                    </div>
                    {homePageCards.map((card) => {
                        const match = card.variable_name.match(/^homepage_card(\d+)$/);
                        const cardIndex = match ? Number(match[1]) : 0;
                        return (
                            <div
                                key={card.variable_name}
                                className="public-card flex flex-col gap-2"
                            >
                                <img
                                    src={`/homepage_cards/${cardIndex}.png`}
                                    alt={appState.t("marketing.homepageCardAlt", { index: cardIndex })}
                                    className="public-card-image img-themed"
                                />
                                <ForwardRefEditor
                                    markdown={stripPlaceholderCtas(card.content)}
                                    readOnly={true}
                                    plugins={[
                                        headingsPlugin(),
                                        listsPlugin(),
                                        quotePlugin(),
                                        thematicBreakPlugin(),
                                        tablePlugin(),
                                        codeBlockPlugin({ defaultCodeBlockLanguage: "" }),
                                        codeMirrorPlugin({ codeBlockLanguages: { "": "Plain Text" } }),
                                    ]}
                                    contentEditableClassName="w-full serif"
                                />
                            </div>
                        );
                    })}
                </>
            )}
        </PublicPageShell>
    )
})